import { useMutation, useQuery } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type { UserSyncRequest } from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

const POLL_MS = 3000;

export function isFinished(request: UserSyncRequest | undefined): boolean {
  return request?.status === "succeeded" || request?.status === "failed";
}

// A second request while one is open returns the open one, so a double click is safe.
export function useRequestUserSync() {
  return useMutation({
    mutationFn: async (companyId: string) => {
      const res = await authorizedFetch(
        `${API_BASE}/companies/${encodeURIComponent(companyId)}/sync`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<UserSyncRequest>;
    },
  });
}

// Polls the request until the worker marks it succeeded or failed.
export function useUserSyncRequest(id: string | null) {
  return useQuery({
    queryKey: ["user-sync", "requests", id],
    enabled: id !== null,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/user-sync/requests/${encodeURIComponent(id ?? "")}`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<UserSyncRequest>;
    },
    refetchInterval: (query) =>
      isFinished(query.state.data) ? false : POLL_MS,
  });
}
